/**
 * Work-order invoice dialog (#1999, #2024): staged card / cash / invoice split, delivery, confirm.
 * Line editing lives on the Payments card.
 *
 * E-invoice (#3071): offered as a delivery method when the workshop has a
 * ready Maventa account. Pre-selected when the bill-to company has a stored
 * e-invoice address or Maventa's registry knows it; shown disabled with the
 * reason when no address can be found.
 *
 * Svea payment link (#2978): offered as a method while the split is a single
 * row. It is not a tender and not part of the split model — the whole total
 * becomes an invoice, the invoice is finalized without an invoice email, and a
 * Svea link for it is created and sent. Money is recorded only when Svea
 * confirms the payment.
 *
 * SumUp terminal (#3790): with terminals in use and the whole amount paid by
 * card and cash, the card goes to a terminal instead of a typed reference.
 * Confirming creates the draft invoice, opens a terminal checkout for it and
 * shows its live state, like the payment dialog does (#3500). A terminal
 * checkout always covers the invoice's whole open amount, so an invoice share
 * keeps the typed reference. If the dialog closes mid-checkout, the work
 * order's "Record payment" resumes it.
 */

import { formatCentsForDisplay as formatEuro } from '@/utils/money';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Link,
  Tooltip,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { FormDialog } from '@/components/common/FormDialog';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';
import { useNotify } from '@/hooks/useNotify';
import {
  printReceiptPdf,
  useCheckoutInvoice,
  useCheckoutWorkOrder,
  useDeliverInvoice,
  useGenerateInvoice,
  useInvoicePreview,
  useSendSveaPayment,
} from '@/hooks/useInvoices';
import { useSveaPaymentLinkStatus } from '@/hooks/useSveaIntegration';
import { useSumUpTerminalStatus } from '@/hooks/useSumUpIntegration';
import {
  defaultReaderId,
  refreshAfterTerminalCheckoutClosed,
  rememberReaderId,
  useOpenTerminalCheckout,
  useOpenTerminalCheckoutForInvoice,
  useStartTerminalCardPayment,
  type TerminalCheckout,
} from '@/hooks/useTerminalCheckout';
import { TerminalCheckoutPanel } from './TerminalCheckoutPanel';
import { einvoiceChoice, useMaventaReceivers } from '@/hooks/useMaventaIntegration';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { billingErrorCode, invoiceIssueStepFailure, translateBillingError } from '@/lib/billingErrors';
import {
  eurosToCents,
  formatCentsAsEuros,
  sanitizeDecimalInput,
} from '@/utils/money';
import {
  isValidCardPaymentReference,
  normalizeCardPaymentReference,
} from '@/utils/cardPaymentReference';
import { normalizePaymentTermsDaysOptions } from '@/features/settings/workshopSettingsPayload';
import {
  applyAmountChange,
  applyMethodChange,
  applyAddRow,
  applyRemoveRow,
  canAddSplitRow,
  availableMethodsForRow,
  createInitialSplit,
  dueDateIsoFromPaymentTermsDays,
  invoiceRemainderCents,
  pickDefaultPaymentTermsDays,
  toCheckoutTenders,
  unallocatedCents,
  type PaymentSplitMethod,
  type PaymentSplitRow,
} from './billing/paymentSplit';

type InvoiceChannel = 'print' | 'email' | 'einvoice';
type SveaChannel = 'email' | 'whatsapp';

/** Select value for the Svea link option; not a PaymentSplitMethod on purpose. */
const SVEA_LINK_OPTION = 'svea_link';

const METHOD_LABEL_KEY = {
  card: 'generate.methodCard',
  cash: 'generate.methodCash',
  invoice: 'generate.methodInvoice',
  other: 'generate.methodOther',
} as const;

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;


/** Non-empty normalized card refs that appear on more than one card tender row. */
function duplicateNormalizedCardReferences(
  rows: readonly PaymentSplitRow[],
  cardReferences: Readonly<Record<string, string>>,
): ReadonlySet<string> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.method !== 'card' || row.amountCents <= 0) continue;
    const normalized = normalizeCardPaymentReference(cardReferences[row.id] ?? '');
    if (normalized === '') continue;
    counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [ref, count] of counts) {
    if (count > 1) duplicates.add(ref);
  }
  return duplicates;
}

export interface GeneratedInvoiceDocuments {
  invoiceId: string;
  receiptId?: string;
}

interface GenerateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  workOrderId: string;
  customerEmail?: string | null;
  taxPercent?: number;
  totalCents?: number;
  vehicleRegistration?: string;
  onCreated?: (documents: GeneratedInvoiceDocuments) => void;
}

export function GenerateInvoiceDialog({
  open,
  onClose,
  workOrderId,
  customerEmail,
  taxPercent = 25.5,
  totalCents,
  vehicleRegistration,
  onCreated,
}: GenerateInvoiceDialogProps) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const paymentMru = useDropdownMru(DROPDOWN_MRU_SELECTORS.paymentMethod);
  const { tenantSettings } = useTenantSettings();

  const generateInvoice = useGenerateInvoice();
  const deliverInvoice = useDeliverInvoice();
  const checkoutWorkOrder = useCheckoutWorkOrder();
  const checkoutInvoice = useCheckoutInvoice();
  const sendSveaLink = useSendSveaPayment();
  // The dialog only opens for invoices.manage, which the Svea routes require too.
  const sveaStatus = useSveaPaymentLinkStatus(open);
  const sveaAvailable = sveaStatus.data?.enabled === true;
  const preview = useInvoicePreview(workOrderId, taxPercent, 0, open);
  const billingCompanyId = preview.data?.billingRecipient?.companyId ?? null;
  // Waits for the preview so the lookup is for the bill-to company, not a guess.
  const receivers = useMaventaReceivers(billingCompanyId, open && preview.data !== undefined);
  const einvoice = einvoiceChoice(receivers.data);
  const queryClient = useQueryClient();
  const terminalStatus = useSumUpTerminalStatus(open);
  const terminalReaders = useMemo(() => terminalStatus.data?.readers ?? [], [terminalStatus.data?.readers]);
  const terminalAvailable = terminalStatus.data?.enabled === true && terminalReaders.length > 0;
  const openTerminalCheckout = useOpenTerminalCheckout();
  const startTerminalCard = useStartTerminalCardPayment();

  const paymentTermsOptions = useMemo(
    () => normalizePaymentTermsDaysOptions(tenantSettings?.paymentTermsDaysOptions),
    [tenantSettings],
  );

  const [rows, setRows] = useState<PaymentSplitRow[]>(() => createInitialSplit(0));
  const [amountInputs, setAmountInputs] = useState<Record<string, string>>({});
  const [channel, setChannel] = useState<InvoiceChannel>('email');
  // Set once the user picks a delivery method, so a late e-invoice answer
  // does not override their choice.
  const [channelTouched, setChannelTouched] = useState(false);
  const [paymentTermsDays, setPaymentTermsDays] = useState(() =>
    pickDefaultPaymentTermsDays(paymentTermsOptions),
  );
  const [notes, setNotes] = useState('');
  const [customerReference, setCustomerReference] = useState(vehicleRegistration ?? '');
  const [receiptEmail, setReceiptEmail] = useState(customerEmail ?? '');
  const [cardReferences, setCardReferences] = useState<Record<string, string>>({});
  const [otherReferences, setOtherReferences] = useState<Record<string, string>>({});
  const [cashReceivedInput, setCashReceivedInput] = useState('');
  const [sveaLinkMode, setSveaLinkMode] = useState(false);
  const [sveaChannel, setSveaChannel] = useState<SveaChannel>('email');
  const [sveaPhone, setSveaPhone] = useState('');
  const idempotencyRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const submittingRef = useRef(false);
  const wasOpenRef = useRef(false);
  // Fallback (PO decision A2): type the terminal reference instead.
  const [manualReference, setManualReference] = useState(false);
  const [readerId, setReaderId] = useState('');
  const [activeCheckoutId, setActiveCheckoutId] = useState<string | null>(null);
  // The draft made for a terminal checkout. A retry reuses it: the work order
  // cannot get a second active invoice.
  const [terminalInvoiceId, setTerminalInvoiceId] = useState<string | null>(null);
  const terminalInvoiceRef = useRef<string | null>(null);
  const terminalCheckoutForInvoice = useOpenTerminalCheckoutForInvoice(terminalInvoiceId ?? undefined, false);

  const estimatedTotalCents = preview.data?.totalCents ?? totalCents ?? 0;
  const leftoverCents = unallocatedCents(estimatedTotalCents, rows);
  const invoiceShareCents = invoiceRemainderCents(rows);
  const hasInvoiceShare = invoiceShareCents > 0;
  const hasCard = rows.some((row) => row.method === 'card' && row.amountCents > 0);
  const hasCash = rows.some((row) => row.method === 'cash' && row.amountCents > 0);
  const hasOther = rows.some((row) => row.method === 'other' && row.amountCents > 0);
  const hasTenders = hasCard || hasCash || hasOther;
  const cashAmountCents = rows.find((row) => row.method === 'cash')?.amountCents ?? 0;
  const cashReceivedCents = eurosToCents(cashReceivedInput) ?? 0;
  const duplicateCardRefs = duplicateNormalizedCardReferences(rows, cardReferences);
  // A terminal checkout freezes the invoice's whole open amount, so cash and
  // cards must cover it; with an invoice share or an "other" row the card
  // keeps a typed reference.
  const terminalEligible = terminalAvailable && hasCard && !hasInvoiceShare && !hasOther && !sveaLinkMode;
  const terminalMode = terminalEligible && !manualReference;
  const firstCardRowId = rows.find((row) => row.method === 'card')?.id;
  const cardValid = terminalMode
    ? readerId !== ''
    : rows
      .filter((row) => row.method === 'card' && row.amountCents > 0)
      .every((row) => {
        const normalized = normalizeCardPaymentReference(cardReferences[row.id] ?? '');
        return isValidCardPaymentReference(normalized)
          && !duplicateCardRefs.has(normalized);
      });
  const cashValid = !hasCash || (cashAmountCents > 0 && cashReceivedCents >= cashAmountCents);
  const otherValid = rows
    .filter((row) => row.method === 'other' && row.amountCents > 0)
    .every((row) => (otherReferences[row.id] ?? '').trim() !== '');
  // The address is needed when the invoice share goes by email, or a Svea link
  // by email. A cash/card receipt alone may go on paper (#3348), so the field
  // shows but stays optional; a typed address must still be valid.
  const invoiceEmailNeeded = hasInvoiceShare && channel === 'email';
  const emailShown = sveaLinkMode ? sveaChannel === 'email' : hasTenders || invoiceEmailNeeded;
  const emailRequired = sveaLinkMode ? sveaChannel === 'email' : invoiceEmailNeeded;
  const receiptEmailTrimmed = receiptEmail.trim();
  const emailValid = emailRequired
    ? EMAIL_PATTERN.test(receiptEmailTrimmed)
    : receiptEmailTrimmed === '' || EMAIL_PATTERN.test(receiptEmailTrimmed);
  const splitComplete = leftoverCents === 0 && rows.every((row) => row.amountCents > 0);
  // A draft made for the terminal blocks a second invoice for the work order:
  // from then on only cash and cards can be taken here, on that draft.
  const draftBlocksSplit = terminalInvoiceId !== null && (hasInvoiceShare || sveaLinkMode);
  const formValid = splitComplete && cardValid && cashValid && otherValid && emailValid && !draftBlocksSplit;
  const isLoading = generateInvoice.isPending
    || sendSveaLink.isPending
    || deliverInvoice.isPending
    || checkoutWorkOrder.isPending
    || checkoutInvoice.isPending
    || openTerminalCheckout.isPending
    || startTerminalCard.isPending;
  // Paper first (#3348): the toast offers the print dialog right after the sale.
  const printReceipt = useCallback(async (receiptId: string): Promise<void> => {
    try {
      const printed = await printReceiptPdf(receiptId);
      if (printed.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
    } catch (error) {
      notify.error(t('payments.openReceiptFailed'), translateBillingError(error, t));
    }
  }, [notify, t, tc]);

  const notifyReceipt = useCallback((receipt: { id: string; receiptNumber: string; recipientEmail: string | null }) => {
    notify.success(
      t('payment.receiptSuccess'),
      receipt.recipientEmail
        ? t('payment.receiptSuccessDesc', { receiptNumber: receipt.receiptNumber, email: receipt.recipientEmail })
        : t('payment.receiptSuccessNoEmailDesc', { receiptNumber: receipt.receiptNumber }),
      {
        action: { label: t('actions.printReceipt'), onClick: () => void printReceipt(receipt.id) },
        duration: 15000,
      },
    );
  }, [notify, printReceipt, t]);

  const isDirty = notes !== ''
    || customerReference !== (vehicleRegistration ?? '')
    || rows.length > 1
    || rows[0]?.method !== 'card'
    || Object.values(cardReferences).some((value) => value !== '')
    || Object.values(otherReferences).some((value) => value !== '')
    || channelTouched
    || sveaLinkMode;

  useEffect(() => {
    const opened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!opened) return;
    setRows(createInitialSplit(estimatedTotalCents));
    setAmountInputs({});
    setChannel('email');
    setChannelTouched(false);
    setNotes('');
    setCustomerReference(vehicleRegistration ?? '');
    setReceiptEmail(customerEmail ?? '');
    setCardReferences({});
    setOtherReferences({});
    setCashReceivedInput('');
    setSveaLinkMode(false);
    setSveaChannel('email');
    setSveaPhone('');
    setPaymentTermsDays(pickDefaultPaymentTermsDays(paymentTermsOptions));
    setManualReference(false);
    setActiveCheckoutId(null);
    setTerminalInvoiceId(null);
    terminalInvoiceRef.current = null;
    idempotencyRef.current = null;
  }, [open, customerEmail, vehicleRegistration, estimatedTotalCents, paymentTermsOptions]);

  // The terminal chosen at this till last time, while it is still paired.
  useEffect(() => {
    if (!open || terminalReaders.length === 0) return;
    setReaderId((current) => defaultReaderId(terminalReaders, current));
  }, [open, terminalReaders]);

  useEffect(() => {
    if (!open || channelTouched) return;
    if (einvoice.preferred) {
      setChannel('einvoice');
    } else if (!einvoice.available) {
      setChannel((current) => (current === 'einvoice' ? 'email' : current));
    }
  }, [open, channelTouched, einvoice.preferred, einvoice.available]);

  useEffect(() => {
    if (!open) return;
    setPaymentTermsDays((current) => (
      paymentTermsOptions.includes(current)
        ? current
        : pickDefaultPaymentTermsDays(paymentTermsOptions)
    ));
  }, [open, paymentTermsOptions]);

  useEffect(() => {
    if (!open) return;
    setRows((current) => {
      if (current.length !== 1) return current;
      const only = current[0];
      if (!only || only.amountCents !== 0 || estimatedTotalCents <= 0) return current;
      return [{ ...only, amountCents: estimatedTotalCents }];
    });
  }, [estimatedTotalCents, open]);

  const bumpCashReceived = (nextRows: PaymentSplitRow[]) => {
    const cash = nextRows.find((row) => row.method === 'cash');
    // Always mirror the cash-row amount. The previous
    // `prevCents < cash.amountCents` guard only ever raised the field, so
    // shrinking the cash share left the full invoice total in "received"
    // and the receipt computed change against that stale value (#2022).
    // A typed overpayment is only meaningful for the current share; if the
    // split changes afterwards the overpayment is stale. Do not restore the
    // one-way bump: it looks like it preserves overpayment, but it cannot
    // tell a leftover full-total from a freshly typed amount. Clearing when
    // the cash row disappears keeps the next cash row from inheriting it.
    if (!cash) {
      setCashReceivedInput('');
      return;
    }
    setCashReceivedInput(formatCentsAsEuros(cash.amountCents));
  };

  const handleAmountChange = (index: number, raw: string) => {
    const row = rows[index];
    if (!row) return;
    const value = sanitizeDecimalInput(raw);
    setAmountInputs({ [row.id]: value });
    const next = applyAmountChange(estimatedTotalCents, rows, index, eurosToCents(value) ?? 0);
    setRows(next);
    bumpCashReceived(next);
  };

  const handleMethodChange = (index: number, method: PaymentSplitMethod) => {
    setSveaLinkMode(false);
    paymentMru.record(method);
    const next = applyMethodChange(rows, index, method);
    setRows(next);
    setAmountInputs({});
    bumpCashReceived(next);
  };

  const handleRemoveRow = (index: number) => {
    const next = applyRemoveRow(estimatedTotalCents, rows, index);
    setRows(next);
    setAmountInputs({});
    bumpCashReceived(next);
  };

  const handleAddRow = () => {
    const next = applyAddRow(rows);
    setRows(next);
    setAmountInputs({});
    bumpCashReceived(next);
  };

  const handleInvoicePath = async (dueDate: string) => {
    // One request creates, finalizes and queues the email or e-invoice
    // (#3112, #3071); the server replays it on the same Idempotency-Key, so a
    // retry cannot double-issue.
    const email = receiptEmail.trim();
    const created = await generateInvoice.mutateAsync({
      workOrderId,
      taxPercent,
      dueDate,
      ...(notes ? { notes } : {}),
      ...(customerReference.trim() ? { customerReference: customerReference.trim() } : {}),
      issue: channel === 'einvoice'
        ? {
            channel: 'einvoice',
            ...(einvoice.pick
              ? { recipientEia: einvoice.pick.eia, recipientOperator: einvoice.pick.operator }
              : {}),
          }
        : {
            channel: channel === 'email' && email ? 'email' : 'manual',
            ...(channel === 'email' && email ? { recipientEmail: email } : {}),
          },
    });
    notify.success(t('generate.success'), t('generate.successDesc', { invoiceNumber: created.invoiceNumber }));
    onClose();
    onCreated?.({ invoiceId: created.id });
  };

  // Svea link: the whole total as an invoice, finalized without the invoice
  // email (the customer gets the link instead), then the link. If the link
  // fails the invoice stays sent and unpaid — it is not rolled back; the link
  // can be created again from the Payments card or the invoice.
  const handleSveaLinkPath = async (dueDate: string) => {
    const created = await generateInvoice.mutateAsync({
      workOrderId,
      taxPercent,
      dueDate,
      ...(notes ? { notes } : {}),
      ...(customerReference.trim() ? { customerReference: customerReference.trim() } : {}),
      issue: { channel: 'manual' },
    });
    const recipient = sveaChannel === 'email' ? receiptEmail.trim() : sveaPhone.trim();
    try {
      await sendSveaLink.mutateAsync({
        invoiceId: created.id,
        channel: sveaChannel,
        ...(recipient ? { recipient } : {}),
      });
      notify.success(
        t('generate.sveaLinkSuccess'),
        t('generate.sveaLinkSuccessDesc', { invoiceNumber: created.invoiceNumber }),
      );
    } catch (err) {
      notify.warning(
        t('generate.sveaLinkFailedTitle', { invoiceNumber: created.invoiceNumber }),
        `${translateBillingError(err, t)} ${t('generate.sveaLinkFailedRetry')}`,
      );
    }
    onClose();
    onCreated?.({ invoiceId: created.id });
  };

  const handleTerminalFinished = useCallback((finished: TerminalCheckout) => {
    // Recorded by a webhook or the sweep as well as by this till: refresh everything.
    refreshAfterTerminalCheckoutClosed(queryClient, finished.invoiceId);
    if (finished.status === 'abandoned') {
      notify.info(t('terminal.abandoned'));
      setActiveCheckoutId(null);
      // A new attempt is a new checkout on the same draft.
      idempotencyRef.current = null;
      return;
    }
    if (finished.status === 'record_failed') {
      // Cards were charged but not recorded: keep the panel open so the till sees it.
      notify.error(t('terminal.actionFailed'), t('terminal.recordFailed'));
      return;
    }
    if (finished.receipt) notifyReceipt(finished.receipt);
    onClose();
    onCreated?.({
      invoiceId: finished.invoiceId,
      ...(finished.receipt ? { receiptId: finished.receipt.id } : {}),
    });
  }, [notify, notifyReceipt, onClose, onCreated, queryClient, t]);

  // Draft invoice → terminal checkout → first card to the terminal. Each
  // step survives a retry: the draft is reused, the open call is replayed on
  // its Idempotency-Key, and a checkout opened elsewhere is shown instead.
  const startTerminalPath = async () => {
    let draftId = terminalInvoiceRef.current;
    if (draftId === null) {
      // No `issue`: the draft is finalized when the checkout is recorded.
      const created = await generateInvoice.mutateAsync({ workOrderId, taxPercent });
      draftId = created.id;
      terminalInvoiceRef.current = draftId;
      setTerminalInvoiceId(draftId);
    }
    const cashRow = rows.find((row) => row.method === 'cash');
    const cash = cashRow?.amountCents ?? 0;
    const cashReceived = cashRow ? cashReceivedCents : 0;
    const fingerprint = JSON.stringify({ terminal: draftId, receiptEmail: receiptEmailTrimmed, cash, cashReceived });
    if (idempotencyRef.current?.fingerprint !== fingerprint) {
      idempotencyRef.current = { fingerprint, key: crypto.randomUUID() };
    }
    rememberReaderId(readerId);
    let opened: TerminalCheckout;
    try {
      opened = await openTerminalCheckout.mutateAsync({
        invoiceId: draftId,
        cashAmountCents: cash,
        cashReceivedCents: cashReceived,
        ...(receiptEmailTrimmed ? { receiptEmail: receiptEmailTrimmed } : {}),
        idempotencyKey: idempotencyRef.current.key,
      });
    } catch (error) {
      if (billingErrorCode(error) === 'terminal_checkout_open') {
        const existing = (await terminalCheckoutForInvoice.refetch()).data;
        if (existing?.status === 'open') {
          setActiveCheckoutId(existing.id);
          return;
        }
      }
      throw error;
    }
    setActiveCheckoutId(opened.id);
    // The first card row's share; the panel offers the rest to the next card.
    // The server froze the draft's real total, which can differ from the preview.
    const firstCard = rows.find((row) => row.method === 'card')?.amountCents ?? 0;
    const amountCents = Math.min(firstCard, opened.remainingCardCents);
    if (amountCents <= 0) return;
    await startTerminalCard.mutateAsync({ checkoutId: opened.id, readerId, amountCents });
  };

  const handleSubmit = async () => {
    if (!formValid) return;
    // Synchronous re-entry guard: the mutation's pending flag only lands on
    // the next render, a double click arrives before that (#3112).
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      if (terminalMode) {
        await startTerminalPath();
        return;
      }
      if (sveaLinkMode) {
        await handleSveaLinkPath(dueDateIsoFromPaymentTermsDays(paymentTermsDays));
        return;
      }
      const tenders = toCheckoutTenders(rows, {
        cardReferences: Object.fromEntries(
          rows
            .filter((row) => row.method === 'card')
            .map((row) => [
              row.id,
              normalizeCardPaymentReference(cardReferences[row.id] ?? ''),
            ]),
        ),
        otherReferences,
        cashReceivedCents,
      });
      const dueDate = hasInvoiceShare
        ? dueDateIsoFromPaymentTermsDays(paymentTermsDays)
        : undefined;
      if (tenders.length === 0) {
        if (!dueDate) return;
        await handleInvoicePath(dueDate);
        return;
      }
      const fingerprint = JSON.stringify({ receiptEmail: receiptEmailTrimmed, tenders });
      if (idempotencyRef.current?.fingerprint !== fingerprint) {
        idempotencyRef.current = { fingerprint, key: crypto.randomUUID() };
      }
      const draftId = terminalInvoiceRef.current;
      const result = draftId !== null
        ? await checkoutInvoice.mutateAsync({
            invoiceId: draftId,
            workOrderId,
            ...(receiptEmailTrimmed ? { receiptEmail: receiptEmailTrimmed } : {}),
            tenders,
            idempotencyKey: idempotencyRef.current.key,
          })
        : await checkoutWorkOrder.mutateAsync({
            workOrderId,
            ...(receiptEmailTrimmed ? { receiptEmail: receiptEmailTrimmed } : {}),
            tenders,
            idempotencyKey: idempotencyRef.current.key,
            taxPercent,
            ...(notes ? { notes } : {}),
            ...(hasInvoiceShare && customerReference.trim()
              ? { customerReference: customerReference.trim() }
              : {}),
            ...(dueDate ? { dueDate } : {}),
          });
      if (hasInvoiceShare && channel === 'einvoice') {
        await deliverInvoice.mutateAsync({
          invoiceId: result.invoiceId,
          workOrderId,
          channel: 'einvoice',
          ...(einvoice.pick
            ? { recipientEia: einvoice.pick.eia, recipientOperator: einvoice.pick.operator }
            : {}),
        });
      } else if (hasInvoiceShare && channel === 'email' && receiptEmail.trim()) {
        await deliverInvoice.mutateAsync({
          invoiceId: result.invoiceId,
          workOrderId,
          recipientEmail: receiptEmail.trim(),
        });
      }
      notifyReceipt(result.receipt);
      onClose();
      const receiptId = result.receipt?.id;
      onCreated?.({
        invoiceId: result.invoiceId,
        ...(typeof receiptId === 'string' && receiptId !== '' ? { receiptId } : {}),
      });
    } catch (err) {
      const stepFailure = invoiceIssueStepFailure(err);
      if (stepFailure) {
        // The invoice exists; say what failed and hand over to the Maksut card.
        notify.error(t('generate.issueStepFailedTitle'), translateBillingError(err, t));
        onClose();
        onCreated?.({ invoiceId: stepFailure.invoiceId });
        return;
      }
      notify.error(t('generate.failedTitle'), translateBillingError(err, t));
    } finally {
      submittingRef.current = false;
    }
  };

  const einvoiceAddress = einvoice.pick?.eia ?? receivers.data?.target?.eia ?? '';
  const einvoiceHelperText = !einvoice.available
    ? t(billingCompanyId ? 'generate.einvoiceNoAddress' : 'generate.einvoiceNoCompany')
    : channel === 'einvoice'
      ? receivers.data?.lookupFailed
        ? t('generate.einvoiceLookupFailed', { address: einvoiceAddress })
        : t('generate.einvoiceTo', { address: einvoiceAddress })
      : null;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      // The dialog does two jobs, and the submit label already says which
      // (#2209). Once money is being recorded this is a payment entry, not
      // invoice creation — the button that opens it says so too (#2071).
      title={hasTenders || sveaLinkMode ? t('payment.entryTitle') : t('generate.title')}
      isDirty={isDirty}
      headerIcon={<ReceiptLongIcon />}
      onSubmit={() => { void handleSubmit(); }}
      submitLabel={sveaLinkMode
        ? t('generate.sveaLinkSubmit')
        : terminalMode ? t('terminal.sendToTerminal')
        : hasTenders ? t('payment.checkoutSubmit') : t('generate.submit')}
      isLoading={isLoading}
      submitDisabled={!formValid || estimatedTotalCents <= 0}
      maxWidth="sm"
      {...(activeCheckoutId !== null
        ? {
            // The checkout lives on the server; closing leaves it running and
            // the work order's "Record payment" resumes it.
            renderActions: ({ onClose: close }: { onClose: () => void }) => (
              <Button onClick={close}>{tc('close')}</Button>
            ),
          }
        : {})}
    >
      <DialogContent>
        {activeCheckoutId !== null ? (
          <TerminalCheckoutPanel
            key={activeCheckoutId}
            checkoutId={activeCheckoutId}
            readers={terminalReaders}
            initialReaderId={readerId}
            onFinished={handleTerminalFinished}
          />
        ) : (
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {preview.isError && (
            <Alert severity="warning">{translateBillingError(preview.error, t)}</Alert>
          )}
          {estimatedTotalCents > 0 && (
            <Alert severity="info">
              {t('edit.totalInclVat')}: {formatEuro(estimatedTotalCents)}
            </Alert>
          )}
          {draftBlocksSplit && (
            <Alert severity="warning">{t('terminal.draftOnlyCashAndCard')}</Alert>
          )}
          {leftoverCents > 0 && (
            <Alert severity="warning" data-testid="split-unallocated">
              {t('generate.unallocated', { amount: formatEuro(leftoverCents) })}
            </Alert>
          )}

          {rows.map((row, index) => {
            const cardReferenceRaw = cardReferences[row.id] ?? '';
            const cardReferenceNormalized = normalizeCardPaymentReference(cardReferenceRaw);
            const rowCardValid = isValidCardPaymentReference(cardReferenceNormalized);
            const rowCardDuplicate = cardReferenceNormalized !== ''
              && duplicateCardRefs.has(cardReferenceNormalized);
            return (
              <Paper
                key={row.id}
                variant="outlined"
                // The remove button floats in the corner rather than sitting in
                // the field row: there it took 36px off the method select the
                // moment a second row appeared, so the fields below it no longer
                // ended at the same edge (#2209).
                sx={{ p: 2, position: 'relative' }}
                data-testid={`payment-split-row-${row.id}`}
              >
                {rows.length > 1 && (
                  <Tooltip title={tc('delete')}>
                    <IconButton
                      aria-label={tc('delete')}
                      onClick={() => handleRemoveRow(index)}
                      data-testid={`payment-split-remove-${row.id}`}
                      size="small"
                      sx={{ position: 'absolute', top: 4, right: 4 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                    <InputLabel id={`split-method-${row.id}`}>{t('payment.method')}</InputLabel>
                    <Select
                      labelId={`split-method-${row.id}`}
                      label={t('payment.method')}
                      value={sveaLinkMode ? SVEA_LINK_OPTION : row.method}
                      onChange={(event) => {
                        const value = event.target.value as PaymentSplitMethod | typeof SVEA_LINK_OPTION;
                        if (value === SVEA_LINK_OPTION) {
                          // Underneath it is the whole total as the invoice
                          // share, so the term, reference and notes apply.
                          const next = applyMethodChange(rows, index, 'invoice');
                          setRows(next);
                          setAmountInputs({});
                          bumpCashReceived(next);
                          setSveaLinkMode(true);
                          return;
                        }
                        handleMethodChange(index, value);
                      }}
                      inputProps={{ 'data-testid': `payment-split-method-${row.id}` }}
                    >
                      {paymentMru.order(
                        availableMethodsForRow(rows, index),
                        (method) => method,
                      ).map((method) => (
                        <MenuItem key={method} value={method}>
                          {t(METHOD_LABEL_KEY[method])}
                        </MenuItem>
                      ))}
                      {sveaAvailable && rows.length === 1 ? (
                        <MenuItem value={SVEA_LINK_OPTION}>{t('generate.methodSveaLink')}</MenuItem>
                      ) : null}
                    </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label={t('generate.payableAmount')}
                  value={amountInputs[row.id] ?? formatCentsAsEuros(row.amountCents)}
                  onChange={(event) => handleAmountChange(index, event.target.value)}
                  disabled={sveaLinkMode}
                  size="small"
                  slotProps={{
                    htmlInput: {
                      'data-testid': `payment-split-amount-${row.id}`,
                      inputMode: 'decimal',
                    },
                  }}
                />
                {row.method === 'card' && terminalMode ? (
                  row.id === firstCardRowId ? (
                    <Stack spacing={0.5}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id={`split-terminal-${row.id}`}>{t('terminal.reader')}</InputLabel>
                        <Select
                          labelId={`split-terminal-${row.id}`}
                          label={t('terminal.reader')}
                          value={readerId}
                          onChange={(event) => setReaderId(event.target.value)}
                          inputProps={{ 'data-testid': `payment-split-terminal-${row.id}` }}
                        >
                          {terminalReaders.map((reader) => (
                            <MenuItem key={reader.id} value={reader.id}>{reader.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary">
                        {t('terminal.sendsAmount', { amount: formatEuro(row.amountCents) })}
                      </Typography>
                      <Link component="button" type="button" variant="body2" sx={{ alignSelf: 'flex-start' }} onClick={() => setManualReference(true)}>
                        {t('terminal.enterReferenceManually')}
                      </Link>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('terminal.nextCardInPanel')}
                    </Typography>
                  )
                ) : row.method === 'card' ? (
                  <>
                  <TextField
                    fullWidth
                    required
                    label={t('payment.cardReference')}
                    value={cardReferenceRaw}
                    onChange={(event) => {
                      const { value } = event.target;
                      setCardReferences((prev) => ({ ...prev, [row.id]: value }));
                    }}
                    size="small"
                    error={!rowCardValid || rowCardDuplicate}
                    helperText={
                      !rowCardValid
                        ? t('payment.cardReferenceInvalid')
                        : rowCardDuplicate
                          ? t('payment.cardReferenceDuplicate')
                          : t('payment.cardReferenceHelp')
                    }
                    slotProps={{
                      htmlInput: {
                        'data-testid': `payment-split-card-reference-${row.id}`,
                      },
                    }}
                  />
                  {terminalEligible && row.id === firstCardRowId ? (
                    <Link component="button" type="button" variant="body2" sx={{ alignSelf: 'flex-start' }} onClick={() => setManualReference(false)}>
                      {t('terminal.backToTerminal')}
                    </Link>
                  ) : terminalAvailable && hasInvoiceShare && !sveaLinkMode && row.id === firstCardRowId ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('terminal.unavailableWithInvoiceShare')}
                    </Typography>
                  ) : terminalAvailable && hasOther && !sveaLinkMode && row.id === firstCardRowId ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('terminal.unavailableWithOther')}
                    </Typography>
                  ) : null}
                  </>
                ) : null}
                {row.method === 'other' ? (
                  <TextField
                    fullWidth
                    required
                    label={t('payment.otherReference')}
                    value={otherReferences[row.id] ?? ''}
                    onChange={(event) => {
                      const { value } = event.target;
                      setOtherReferences((prev) => ({ ...prev, [row.id]: value }));
                    }}
                    size="small"
                    helperText={t('payment.otherReferenceHelp')}
                    slotProps={{ htmlInput: { maxLength: 100, 'data-testid': `payment-split-other-reference-${row.id}` } }}
                  />
                ) : null}
                {row.method === 'cash' ? (
                  <TextField
                    fullWidth
                    label={t('payment.cashReceived')}
                    value={cashReceivedInput}
                    onChange={(event) => setCashReceivedInput(sanitizeDecimalInput(event.target.value))}
                    size="small"
                    error={cashReceivedCents > 0 && cashReceivedCents < cashAmountCents}
                    helperText={t('payment.change', {
                      amount: formatEuro(Math.max(0, cashReceivedCents - cashAmountCents)),
                    })}
                  />
                ) : null}
              </Stack>
              </Paper>
            );
          })}

          {/* Splitting a payment used to be a side effect of shrinking an
              amount, and the row it created could never be a second card
              (#2209). */}
          {canAddSplitRow(rows) && !sveaLinkMode && (
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={handleAddRow}
              data-testid="payment-split-add"
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('payment.addSplitRow')}
            </Button>
          )}

          {hasInvoiceShare ? (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel id="payment-term-label">{t('generate.paymentTerm')}</InputLabel>
                <Select
                  labelId="payment-term-label"
                  label={t('generate.paymentTerm')}
                  value={paymentTermsDays}
                  onChange={(event) => setPaymentTermsDays(Number(event.target.value))}
                  inputProps={{ 'data-testid': 'payment-terms-select' }}
                >
                  {paymentTermsOptions.map((days) => (
                    <MenuItem key={days} value={days}>
                      {t('generate.paymentTermDays', { count: days })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t('edit.customerReference')}
                value={customerReference}
                onChange={(event) => setCustomerReference(event.target.value)}
                size="small"
              />
              {!sveaLinkMode ? (
              <FormControl size="small" fullWidth>
                <InputLabel id="delivery-method-label">{t('generate.deliveryMethod')}</InputLabel>
                <Select
                  labelId="delivery-method-label"
                  label={t('generate.deliveryMethod')}
                  value={channel}
                  onChange={(event) => {
                    setChannelTouched(true);
                    setChannel(event.target.value as InvoiceChannel);
                  }}
                  inputProps={{ 'data-testid': 'delivery-method-select' }}
                >
                  <MenuItem value="print">{t('generate.channelPrint')}</MenuItem>
                  <MenuItem value="email">{t('generate.channelEmail')}</MenuItem>
                  {einvoice.visible ? (
                    <MenuItem value="einvoice" disabled={!einvoice.available}>
                      {t('generate.channelEinvoice')}
                    </MenuItem>
                  ) : null}
                </Select>
                {einvoice.visible && einvoiceHelperText !== null ? (
                  <FormHelperText data-testid="einvoice-helper">
                    {einvoiceHelperText}
                  </FormHelperText>
                ) : null}
              </FormControl>
              ) : null}
              <TextField
                fullWidth
                label={t('generate.notes')}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                size="small"
                multiline
                rows={2}
              />
            </>
          ) : null}

          {sveaLinkMode ? (
            <>
              <RadioGroup
                row
                value={sveaChannel}
                onChange={(event) => setSveaChannel(event.target.value as SveaChannel)}
                aria-label={t('payment.sveaLinkChannel')}
              >
                <FormControlLabel value="email" control={<Radio size="small" />} label={t('payment.sveaLinkChannelEmail')} />
                <FormControlLabel value="whatsapp" control={<Radio size="small" />} label={t('payment.sveaLinkChannelWhatsapp')} />
              </RadioGroup>
              {sveaChannel === 'email' ? (
                <TextField
                  fullWidth
                  required
                  type="email"
                  label={t('payment.sveaLinkEmail')}
                  value={receiptEmail}
                  onChange={(event) => setReceiptEmail(event.target.value)}
                  size="small"
                  error={receiptEmail !== '' && !emailValid}
                />
              ) : (
                <TextField
                  fullWidth
                  type="tel"
                  label={t('payment.sveaLinkPhone')}
                  value={sveaPhone}
                  onChange={(event) => setSveaPhone(event.target.value)}
                  size="small"
                  helperText={t('payment.sveaLinkPhoneHelp')}
                />
              )}
              <Alert severity="info">{t('generate.sveaLinkNotice')}</Alert>
            </>
          ) : emailShown ? (
            <>
              <TextField
                fullWidth
                required={emailRequired}
                type="email"
                label={t(invoiceEmailNeeded ? 'payment.invoiceEmail' : 'payment.receiptEmail')}
                value={receiptEmail}
                onChange={(event) => setReceiptEmail(event.target.value)}
                size="small"
                error={receiptEmail !== '' && !emailValid}
                helperText={t(invoiceEmailNeeded ? 'payment.invoiceEmailHelp' : 'payment.receiptEmailHelp')}
              />
              {hasTenders ? (
                <Alert severity="info">
                  {receiptEmailTrimmed ? t('payment.automaticEmailNotice') : t('payment.paperReceiptNotice')}
                </Alert>
              ) : null}
            </>
          ) : null}
        </Stack>
        )}
      </DialogContent>
    </FormDialog>
  );
}
